import type { MilestoneBody, MilestonesOverviewResponse } from '@pulse/contracts';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { AsOfPill } from '@/components/pulse/day';
import { Kpis } from '@/components/pulse/kpis';
import { PageHeader } from '@/components/pulse/page-header';
import { SourceLink } from '@/components/pulse/source-link';
import { useCreateMilestone, useMilestonesOverview } from '@/domains/milestones/api';
import { useProjectsList } from '@/domains/projects/api';
import { Chip, Meter, MilestoneForm } from '@/domains/projects/project-milestones-page';
import { useShell } from '@/domains/shell/api';
import { ApiRequestError } from '@/lib/api-client';

type Milestone = MilestonesOverviewResponse['milestones'][number];

/** MS-6's groups, most urgent first, and what each means. */
const GROUPS: ReadonlyArray<readonly [Milestone['state'], string]> = [
  ['Late', 'Past its date with work left.'],
  ['At risk', 'The forecast lands after its date.'],
  ['On track', 'On pace for its date.'],
  ['Done', 'Every item done; close it when it has shipped.'],
];
const warns = (state: string) => state === 'Late' || state === 'At risk';

/** Milestones (MS-6), built from the prototype's `viewAllMilestones()`. As of now. */
export function MilestonesPage(): React.ReactElement {
  const page = useMilestonesOverview();
  const shell = useShell();
  const [project, setProject] = React.useState<string | null>(null);
  const [creating, setCreating] = React.useState(false);
  return (
    <>
      <PageHeader
        eyebrow="Milestones · as of now"
        title="Milestones"
        sub="Every project's open milestones, the ones needing attention first. Talos fills each one with its stories and tasks and forecasts it from the project's pace."
        right={
          <>
            <button type="button" className="btn primary" onClick={() => setCreating(true)}>
              New milestone
            </button>
            <AsOfPill label={shell.data?.freshness.asOfLabel ?? null} />
          </>
        }
      />
      {creating ? <NewMilestone preferred={project} onDone={() => setCreating(false)} /> : null}
      {page.data !== undefined ? <MilestonesOverviewView data={page.data} project={project} onProject={setProject} /> : null}
    </>
  );
}

/** MS-6: New milestone asks for the project first, from every project in scope. */
function NewMilestone({ preferred, onDone }: { readonly preferred: string | null; readonly onDone: () => void }): React.ReactElement | null {
  const projects = useProjectsList();
  const create = useCreateMilestone();
  const error = create.error instanceof ApiRequestError ? create.error.toUserMessage() : create.error instanceof Error ? create.error.message : null;
  const options = (projects.data?.projects ?? []).map(({ slug, name }) => ({ slug, name }));
  if (options.length === 0) return null;
  // The project the page is filtered to comes first.
  const ordered = [...options.filter((option) => option.slug === preferred), ...options.filter((option) => option.slug !== preferred)];
  return (
    <div style={{ marginBottom: 16 }}>
      <MilestoneForm
        milestone={null}
        projects={ordered}
        busy={create.isPending}
        error={error}
        onCancel={onDone}
        onSave={(body: MilestoneBody, project: string) => create.mutate({ project, body }, { onSuccess: onDone })}
      />
    </div>
  );
}

export function MilestonesOverviewView({
  data,
  project,
  onProject,
}: {
  readonly data: MilestonesOverviewResponse;
  readonly project: string | null;
  readonly onProject: (project: string | null) => void;
}): React.ReactElement {
  const inView = (milestone: { readonly project: { readonly slug: string } }) => project === null || milestone.project.slug === project;
  const open = data.milestones.filter(inView);
  const closed = data.closed.filter(inView);
  const late = open.filter((milestone) => milestone.state === 'Late');
  const atRisk = open.filter((milestone) => milestone.state === 'At risk');
  const soon = open.filter((milestone) => milestone.dueSoon);
  const projectName = data.projects.find((candidate) => candidate.slug === project)?.name;
  return (
    <>
      <Kpis
        items={[
          { label: 'Open milestones', value: open.length, sub: projectName ?? `Across ${new Set(open.map((milestone) => milestone.project.slug)).size} projects` },
          { label: 'Late', value: late.length, sub: late.length > 0 ? [...new Set(late.map((milestone) => milestone.project.name))].join(', ') : 'None past its date', warn: late.length > 0 },
          { label: 'At risk', value: atRisk.length, sub: atRisk.length > 0 ? 'Forecast after the date' : 'Every forecast is on time', warn: atRisk.length > 0 },
          { label: 'Due in the next three weeks', value: soon.length, sub: soon.length > 0 ? `By ${soon.at(-1)?.dateLabel ?? ''}` : 'Nothing due soon' },
        ]}
      />
      {data.projects.length > 1 ? (
        <div className="row" style={{ marginBottom: 16 }}>
          <div className="seg" role="group" aria-label="Project" style={{ flexWrap: 'wrap' }}>
            <button type="button" aria-pressed={project === null} onClick={() => onProject(null)}>
              All projects
            </button>
            {data.projects.map((candidate) => (
              <button key={candidate.slug} type="button" aria-pressed={project === candidate.slug} onClick={() => onProject(candidate.slug)}>
                {candidate.name}
              </button>
            ))}
          </div>
        </div>
      ) : null}
      <div className="two" style={{ gridTemplateColumns: 'minmax(0,1fr) 320px', alignItems: 'start' }}>
        <div className="stack" style={{ gap: 18 }}>
          {open.length === 0 ? (
            <section className="card pad">
              <span style={{ color: 'var(--muted)' }}>No open milestone{projectName === undefined ? '' : ` on ${projectName}`}. Add one to plan against.</span>
            </section>
          ) : null}
          {GROUPS.map(([state, why]) => {
            const list = open.filter((milestone) => milestone.state === state);
            return list.length === 0 ? null : (
              <section key={state} className="stack" style={{ gap: 10 }} aria-label={state}>
                <div className="row" style={{ gap: 8, alignItems: 'baseline' }}>
                  <span className="eyebrow">
                    {state} · {list.length}
                  </span>
                  <span style={{ fontSize: 13, color: 'var(--muted)' }}>{why}</span>
                </div>
                {list.map((milestone) => (
                  <MilestoneRowCard key={`${milestone.project.slug}/${milestone.id}`} milestone={milestone} />
                ))}
              </section>
            );
          })}
          {closed.length > 0 ? (
            <span style={{ fontSize: 13, color: 'var(--muted)' }}>
              {closed.length} closed {closed.length === 1 ? 'milestone' : 'milestones'}:{' '}
              {closed.map((milestone, index) => (
                <React.Fragment key={`${milestone.project.slug}/${milestone.id}`}>
                  {index > 0 ? ', ' : ''}
                  <Link to={`/projects/${milestone.project.slug}/milestones`}>{milestone.title}</Link>
                </React.Fragment>
              ))}
              .
            </span>
          ) : null}
        </div>
        <aside className="stack" style={{ gap: 16 }}>
          <section className="card pad stack" style={{ gap: 8 }}>
            <h2>Coming up</h2>
            {open.length === 0 ? <span style={{ color: 'var(--muted)' }}>Nothing open.</span> : null}
            {open.map((milestone) => (
              <Link
                key={`${milestone.project.slug}/${milestone.id}`}
                className="prow"
                to={`/projects/${milestone.project.slug}/milestones`}
                style={{ gridTemplateColumns: '56px minmax(0,1fr)', textDecoration: 'none', color: 'var(--ink)', padding: '8px 0', alignItems: 'start' }}
              >
                <span className="num" style={{ fontSize: 13, fontWeight: 600 }}>
                  {milestone.dateLabel.replace(/^\w+ /, '')}
                </span>
                <span className="stack" style={{ gap: 2, minWidth: 0 }}>
                  <span>{milestone.title}</span>
                  <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                    {milestone.project.name} ·{' '}
                    <span style={{ color: milestone.state === 'Late' ? 'var(--bad-ink)' : milestone.state === 'At risk' ? 'var(--warn-ink)' : 'var(--muted)', fontWeight: warns(milestone.state) ? 600 : 400 }}>
                      {milestone.state}
                    </span>
                  </span>
                </span>
              </Link>
            ))}
          </section>
          <section className="card pad stack" style={{ gap: 8 }}>
            <h2>How the forecast works</h2>
            <span style={{ color: 'var(--ink-2)' }}>
              The forecast is today plus the work remaining at the project&apos;s pace over the last four weeks. A milestone is at risk when the forecast is after its date, and late when its date has passed with work left.
            </span>
          </section>
          <section className="card pad stack" style={{ gap: 8 }}>
            <h2>Where milestones come from</h2>
            <span style={{ color: 'var(--ink-2)' }}>
              Pulse milestones are added here or on a project&apos;s Milestones tab. Teamwork milestones follow Teamwork. Owners of items on a late or at-risk milestone see them in their inbox and the 8 AM email.
            </span>
          </section>
        </aside>
      </div>
    </>
  );
}

function MilestoneRowCard({ milestone }: { readonly milestone: Milestone }): React.ReactElement {
  const id = `am-${milestone.project.slug}-${milestone.id}`;
  return (
    <article className="card pad stack" style={{ gap: 10 }} aria-labelledby={id}>
      <div className="row" style={{ justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div className="stack" style={{ gap: 3, minWidth: 0 }}>
          <Link to={`/projects/${milestone.project.slug}`} style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', textDecoration: 'none', textTransform: 'uppercase', letterSpacing: '.04em' }}>
            {milestone.project.name}
          </Link>
          <Link id={id} to={`/projects/${milestone.project.slug}/milestones`} style={{ fontSize: 17, fontWeight: 650, color: 'var(--ink)', textDecoration: 'none' }}>
            {milestone.title}
          </Link>
        </div>
        <span className="row" style={{ gap: 6 }}>
          {milestone.source === 'Teamwork' ? <Chip state="Teamwork" /> : null}
          <Chip state={milestone.state} />
        </span>
      </div>
      <div className="row num" style={{ gap: 22, flexWrap: 'wrap', fontSize: 13, color: 'var(--muted)' }}>
        <span>
          Due <b style={{ color: 'var(--ink)', fontWeight: 600 }}>{milestone.dateLabel}</b>
        </span>
        <span>
          Forecast <b style={{ color: warns(milestone.state) ? 'var(--warn-ink)' : 'var(--ink)', fontWeight: 600 }}>{milestone.forecastLabel}</b>
        </span>
        <span>
          <b style={{ color: 'var(--ink)', fontWeight: 600 }}>
            {milestone.done} of {milestone.total}
          </b>{' '}
          done
        </span>
      </div>
      <Meter done={milestone.done} total={milestone.total} state={milestone.state} />
      {milestone.talosLine !== null ? (
        <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>
          <b style={{ fontWeight: 600 }}>Talos:</b> {milestone.talosLine}
        </span>
      ) : null}
      {milestone.nextItems.length > 0 ? (
        <span style={{ fontSize: 13, color: 'var(--muted)' }}>
          Still to do:{' '}
          {milestone.nextItems.map((item, index) => (
            <React.Fragment key={item.ref}>
              {index > 0 ? '; ' : ''}
              <SourceLink href={item.url} className="mono" style={{ fontSize: 12 }}>
                {item.ref}
              </SourceLink>{' '}
              {item.title}
            </React.Fragment>
          ))}
          {milestone.remaining > milestone.nextItems.length ? `, and ${milestone.remaining - milestone.nextItems.length} more` : ''}
        </span>
      ) : null}
    </article>
  );
}
